import { useState } from "react";
import { useDynamicContent } from "../lib/dynamicContent";
import { type Language } from "../lib/translations";
import { ShieldAlert } from "lucide-react";
import { ProjectsSkeleton } from "./ui/Skeletons";
import { ProjectCard, type ProjectItem } from "./ProjectCard";
import { ProjectDetailsModal } from "./ProjectDetailsModal";
import { ProjectSearchFilters } from "./ProjectSearchFilters";

// Inline CSS for the infinite marquee
const MARQUEE_STYLE = `
  @keyframes marquee {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
  .animate-marquee-track {
    display: flex;
    width: max-content;
    animation: marquee 25s linear infinite;
  }
  .animate-marquee-track:hover {
    animation-play-state: paused;
  }
`;

interface ProjectsHeaderProps {
  headerText: string;
  summaryText: string;
}

function ProjectsHeader({ headerText, summaryText }: ProjectsHeaderProps) {
  return (
    <section className="text-center md:text-left space-y-4 max-w-3xl">
      <h3 className="text-3xl font-extrabold tracking-tight text-brand-primary">
        {headerText}
      </h3>
      <p className="text-base text-foreground/80 leading-relaxed font-sans">
        {summaryText}
      </p>
    </section>
  );
}

interface ProjectsTickerProps {
  tickerItems: string[];
}

function ProjectsTicker({ tickerItems }: ProjectsTickerProps) {
  return (
    <section className="relative overflow-hidden w-full py-4 border-y border-border/40 bg-card/20 backdrop-blur-sm rounded-xl">
      <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
      <div className="absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />

      <div className="animate-marquee-track gap-8 flex items-center">
        {[...tickerItems, ...tickerItems].map((tech, idx) => (
          <div
            key={idx}
            className="px-4 py-2 text-xs font-bold font-poppins rounded-full border border-border/40 bg-card/45 shadow-sm text-brand-primary hover:border-brand-primary/40 transition-colors pointer-events-auto"
          >
            {tech}
          </div>
        ))}
      </div>
    </section>
  );
}

interface ProjectsGridProps {
  filteredProjects: ProjectItem[];
  lang: Language;
  setSelectedProject: (proj: ProjectItem) => void;
}

function ProjectsGrid({ filteredProjects, lang, setSelectedProject }: ProjectsGridProps) {
  if (filteredProjects.length === 0) {
    return (
      <div className="text-center py-16 border border-dashed rounded-2xl border-border/60 bg-card/25">
        <ShieldAlert className="h-10 w-10 mx-auto text-muted-foreground opacity-60 mb-3" />
        <p className="text-sm text-muted-foreground font-semibold">
          {lang === "tr" ? "Arama kriterlerine uygun proje bulunamadı." : "No projects found matching your criteria."}
        </p>
      </div>
    );
  }

  return (
    <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {filteredProjects.map((project: ProjectItem) => (
        <ProjectCard
          key={project.id}
          project={project}
          lang={lang}
          onClick={() => setSelectedProject(project)}
        />
      ))}
    </section>
  );
}

function useProjectsFilters(projects: ProjectItem[], lang: Language) {
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [appliedFilters, setAppliedFilters] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<string>("newest");

  const sortedProjects = getFilteredAndSortedProjects(
    projects,
    searchQuery,
    appliedFilters,
    sortBy,
    lang
  );

  const uniqueTech = Array.from(
    new Set(
      projects.flatMap((p: ProjectItem) =>
        (p.tech_stack || "").split(",").map((s) => s.trim()).filter(Boolean)
      )
    )
  ).sort();

  const tickerItems = uniqueTech.length > 0 ? uniqueTech : ["React", "Bun", "TypeScript", "SQLite", "Tailwind CSS"];

  const toggleFilter = (tech: string) => {
    setSelectedFilters((prev) =>
      prev.includes(tech) ? prev.filter((t) => t !== tech) : [...prev, tech]
    );
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchQuery(searchInput);
  };

  const handleApplyFilters = () => {
    setAppliedFilters(selectedFilters);
  };

  const handleReset = () => {
    setSearchInput("");
    setSearchQuery("");
    setSelectedFilters([]);
    setAppliedFilters([]);
    setSortBy("newest");
  };

  return {
    searchInput,
    setSearchInput,
    searchQuery,
    selectedFilters,
    appliedFilters,
    sortBy,
    setSortBy,
    sortedProjects,
    uniqueTech,
    tickerItems,
    toggleFilter,
    handleSearchSubmit,
    handleApplyFilters,
    handleReset,
  };
}

export function ProjectsTab({ lang }: { lang: Language }) {
  const { projects = [], settings = [], loading } = useDynamicContent();
  const [selectedProject, setSelectedProject] = useState<ProjectItem | null>(null);

  const {
    searchInput,
    setSearchInput,
    searchQuery,
    selectedFilters,
    appliedFilters,
    sortBy,
    setSortBy,
    sortedProjects,
    uniqueTech,
    tickerItems,
    toggleFilter,
    handleSearchSubmit,
    handleApplyFilters,
    handleReset,
  } = useProjectsFilters(projects, lang);

  // Retrieve projects page configs
  const headerText = settings.find((s) => s.key === `projects_header_${lang}`)?.value ||
    (lang === "tr" ? "Projeler" : "Projects");
  const summaryText = settings.find((s) => s.key === `projects_summary_${lang}`)?.value ||
    (lang === "tr" ? "Mühendislik projelerimi ve teknik çalışmalarımı buradan inceleyebilirsiniz." : "A showcase of engineering products, tools, and technical research I have built.");

  if (loading) {
    return <ProjectsSkeleton />;
  }

  return (
    <div className="space-y-12 max-w-7xl mx-auto py-6 animate-fade-in text-left">
      <style>{MARQUEE_STYLE}</style>

      <ProjectsHeader headerText={headerText} summaryText={summaryText} />

      <ProjectsTicker tickerItems={tickerItems} />

      {/* Search & Filter Controls */}
      <ProjectSearchFilters
        lang={lang}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        searchQuery={searchQuery}
        appliedFilters={appliedFilters}
        selectedFilters={selectedFilters}
        toggleFilter={toggleFilter}
        uniqueTech={uniqueTech}
        handleSearchSubmit={handleSearchSubmit}
        handleApplyFilters={handleApplyFilters}
        handleReset={handleReset}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />

      {/* Projects Grid Display */}
      <ProjectsGrid
        filteredProjects={sortedProjects}
        lang={lang}
        setSelectedProject={setSelectedProject}
      />

      {/* Details Modal */}
      {selectedProject && (
        <ProjectDetailsModal
          project={selectedProject}
          lang={lang}
          onClose={() => setSelectedProject(null)}
        />
      )}
    </div>
  );
}

function getFilteredAndSortedProjects(
  projects: ProjectItem[],
  searchQuery: string,
  appliedFilters: string[],
  sortBy: string,
  lang: Language
): ProjectItem[] {
  const filtered = projects.filter((project: ProjectItem) => {
    const matchesSearch = searchQuery
      ? project.title_en.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.title_tr.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.summary_en.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.summary_tr.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.tech_stack.toLowerCase().includes(searchQuery.toLowerCase())
      : true;

    const techList = (project.tech_stack || "").split(",").map((t) => t.trim()).filter(Boolean);
    const matchesTech = appliedFilters.length > 0
      ? techList.some((tech) => appliedFilters.includes(tech))
      : true;

    return matchesSearch && matchesTech;
  });

  return [...filtered].sort((a, b) => {
    if (sortBy === "alphabetical") {
      const titleA = lang === "tr" ? a.title_tr : a.title_en;
      const titleB = lang === "tr" ? b.title_tr : b.title_en;
      return titleA.localeCompare(titleB);
    }
    const timeA = a.date ? new Date(a.date).getTime() : 0;
    const timeB = b.date ? new Date(b.date).getTime() : 0;
    const cleanA = Number.isNaN(timeA) ? 0 : timeA;
    const cleanB = Number.isNaN(timeB) ? 0 : timeB;
    if (sortBy === "oldest") {
      return cleanA - cleanB;
    }
    return cleanB - cleanA;
  });
}

export default ProjectsTab;
